// src/components/strategies/StrategyManager.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { saveStrategy, ApiError } from '../../api/apiClient';
import { HiSave, HiCheckCircle } from 'react-icons/hi';

const StrategyManager = ({ activeParams }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  
  const [customStrategyName, setCustomStrategyName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState({ loading: false, success: null, error: null });

  // Обновляем название по умолчанию при изменении активных параметров
  useEffect(() => {
    if (activeParams?.symbol_a && activeParams?.symbol_b) {
      setCustomStrategyName(`${activeParams.symbol_a} / ${activeParams.symbol_b} ${activeParams.timeframe}m`);
      setDescription('');
    }
  }, [activeParams]);

  const handleSave = async () => {
    if (!activeParams) { 
      setStatus({ error: t('manager.error_no_params'), success: null, loading: false }); 
      return; 
    }
    if (!customStrategyName.trim()) { 
      setStatus({ error: t('manager.error_no_name'), success: null, loading: false }); 
      return; 
    }
    
    setStatus({ loading: true, error: null, success: null });
    
    const payload = {
      strategyName: "pairs_trading", 
      customStrategyName: customStrategyName.trim(),
      parameters: {
        description: description.trim(),
        strategy_method: activeParams.strategy_method,
        symbol_a: activeParams.symbol_a, 
        symbol_b: activeParams.symbol_b,
        timeframe: String(activeParams.timeframe), 
        zscore_period: Number(activeParams.zscore_period),
        bar: Number(activeParams.bar), 
        // --- ДОБАВЛЕННЫЕ ПОЛЯ ---
        margin: Number(activeParams.margin),
        leverage: Number(activeParams.leverage),
        // ------------------------
        total_usdt_one_entry: Number(activeParams.total_usdt_one_entry),
        commission_rate: Number(activeParams.commission_rate), 
        exit: Number(activeParams.exit),
        level_entry_positive: activeParams.level_entry?.level_entry_positive?.map(Number) || [],
        level_entry_negative: activeParams.level_entry?.level_entry_negative?.map(Number) || [],
      }
    };

    try {
      const response = await saveStrategy(payload);
      setStatus({ success: response.message || "Saved!", loading: false, error: null });
      // Очищаем статус успеха через 3 секунды
      setTimeout(() => setStatus({ success: null, loading: false, error: null }), 3000);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : t('manager.error_unknown');
      setStatus({ error: msg, loading: false, success: null });
    }
  };

  return (
    <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700/50">
      <h3 className="text-xl font-semibold text-white mb-4">{t('manager.add_title')}</h3>
      <div className="space-y-4">
        <input 
          type="text" 
          placeholder={t('manager.name_placeholder')} 
          value={customStrategyName} 
          onChange={(e) => setCustomStrategyName(e.target.value)} 
          className="w-full bg-slate-700 border border-slate-600 rounded-lg p-2.5 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500" 
        />
        <textarea 
          placeholder={t('manager.desc_placeholder')} 
          value={description} 
          onChange={(e) => setDescription(e.target.value)} 
          rows="3" 
          className="w-full bg-slate-700 border border-slate-600 rounded-lg p-2.5 text-white placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" 
        />
        <button 
          onClick={handleSave} 
          disabled={status.loading || !activeParams || !customStrategyName.trim()} 
          className="w-full flex items-center justify-center py-2.5 font-semibold rounded-lg text-white bg-green-600 hover:bg-green-700 disabled:bg-slate-600 disabled:cursor-not-allowed transition-colors"
        >
          <HiSave className="w-5 h-5 mr-2" />
          {status.loading ? t('manager.saving') : t('manager.save_btn')}
        </button>
        
        {status.success && (
          <p className="text-sm text-green-400 flex items-center animate-fade-in">
            <HiCheckCircle className="mr-1"/>{status.success}
          </p>
        )}
        {status.error && (
          <p className="text-sm text-red-400 animate-fade-in">
            {status.error}
          </p>
        )}
      </div>
    </div>
  );
};

export default StrategyManager;